import ChevronRight from '@ixigo/icons/ChevronRight';
import Collapse from '@ixigo/iui/Collapse';
import { IconButton } from '@ixigo/iui/IconButton';
import { AnimateSize } from '@repo/shared/components';
import cn from '@repo/shared/utils/cn';
import { useState } from 'react';
import type { BaseFareSummary } from '@/api';
import { SectionHeader } from '../section-header';
import { FareItemCollapsibleGroup, FareItemGroup } from './FareGroup';
import type { DiscountFareItem } from './index.types';
import { LineItem, type LineItemProps } from './LineItem';

export type FareSummaryRowItem = BaseFareSummary &
	Partial<
		Pick<
			LineItemProps,
			'size' | 'labelWeight' | 'valueWeight' | 'labelColor' | 'valueColor'
		>
	>;

export interface FareSummaryProps {
	displayCharges: BaseFareSummary[];
	discounts?: DiscountFareItem[];
	fareAdjustmentLineItems?: DiscountFareItem[];
	paidCharges?: FareSummaryRowItem[];
	toBePaidCharges?: FareSummaryRowItem[];

	// TODO: enum value should not be PascalCase. also when two values are possible, use a boolean instead
	mode?: 'view' | 'Collapsible';
	expanded?: boolean;
	children?: React.ReactNode;
	className?: string;
	/**
	 * optional header component displayed below section title
	 */
	header?: React.ReactNode;
	/**
	 * optional footnote rendered inside the fare-summary card with a 15px gap
	 * from the breakdown — distinct from `children` which sits outside with a
	 * 20px gap.
	 */
	footnote?: React.ReactNode;
}

export function FareSummary({
	displayCharges = [],
	discounts = [],
	fareAdjustmentLineItems,
	paidCharges = [],
	toBePaidCharges = [],
	mode = 'view',
	expanded = true,
	children = null,
	header,
	footnote,
	className = '',
}: FareSummaryProps) {
	const [isExpanded, setIsExpanded] = useState(
		mode !== 'Collapsible' || expanded,
	);

	const hasFareAdjustmentLineItems =
		fareAdjustmentLineItems && fareAdjustmentLineItems.length > 0;

	return (
		<div className={cn('bg-primary flex flex-col gap-y-20 p-20', className)}>
			<div className="flex flex-col gap-y-15">
				<div className={cn('flex flex-col')} id="fare-summary">
					<div className="flex items-center justify-between">
						<SectionHeader title="Fare Summary" />
						{mode === 'Collapsible' && (
							<IconButton onClick={() => setIsExpanded(!isExpanded)} size="sm">
								<ChevronRight
									className={cn(
										'transition-transform duration-300',
										isExpanded ? '-rotate-90' : 'rotate-90',
									)}
								/>
							</IconButton>
						)}
					</div>
					{header}
					<div className={cn('flex flex-col', isExpanded ? 'gap-y-10' : '')}>
						{paidCharges.length > 0 && (
							<FareItemGroup className="border-none pt-0">
								{paidCharges.map((item, index) => (
									<LineItem key={index} size="lg" {...item} />
								))}
							</FareItemGroup>
						)}
						<Collapse in={isExpanded}>
							<div
								className={cn(
									'flex flex-col py-10',
									paidCharges.length > 0
										? 'border-t border-tertiary'
										: 'border-none',
								)}
							>
								{displayCharges.map((item, index) => {
									if (item.breakup?.length) {
										return (
											<FareItemCollapsibleGroup
												label={item.label}
												value={item.value}
												key={index}
											>
												{item.breakup.map((breakUpItem, breakUpIndex) => (
													<LineItem key={breakUpIndex} {...breakUpItem} />
												))}
											</FareItemCollapsibleGroup>
										);
									}
									return (
										<LineItem
											key={index}
											label={item.label}
											value={item.value}
										/>
									);
								})}
							</div>
							<AnimateSize>
								{discounts.length > 0 ? (
									<FareItemGroup
										className={hasFareAdjustmentLineItems ? 'pb-10' : ''}
									>
										{discounts.map((item, index) => (
											<LineItem key={index} {...item} />
										))}
									</FareItemGroup>
								) : null}
							</AnimateSize>
							{hasFareAdjustmentLineItems ? (
								<FareItemGroup className="border-t border-tertiary">
									{fareAdjustmentLineItems.map((item, index) => (
										<LineItem key={index} {...item} />
									))}
								</FareItemGroup>
							) : null}
						</Collapse>
						{toBePaidCharges.length > 0 && (
							<FareItemGroup
								className={cn('gap-10', isExpanded ? '' : 'border-none')}
							>
								{toBePaidCharges.map((item, index) => (
									<LineItem key={index} size="lg" {...item} />
								))}
							</FareItemGroup>
						)}
					</div>
				</div>
				{footnote}
			</div>
			{children}
		</div>
	);
}

export default FareSummary;
